import { Coffee, LogIn, LogOut, Play } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/library/dropdown-menu";
import { cn } from "@/lib/utils";
import { useIsImpersonating } from "@/features/auth/impersonation/ImpersonationBanner";
import CheckInStatusPill from "@/features/checkIn/components/CheckInStatusPill";
import { useCheckInMutation } from "@/features/checkIn/api/useCheckIn";
import { HR_KEYS, useHrEnabled, useHrMe } from "../../api/hrApi";
import { fmtClockCompact, fmtHours } from "../../utils/hrFormat";
import { usePunch, usePunchState } from "../api/usePunch";
import { minutesSince } from "../api/hrQuery";
import { useNow } from "../hooks/useNow";

/**
 * THE ONE CHECK-IN BUTTON (header).
 *
 * With the HR suite on, one tap records attendance AND makes the person
 * available for automated leads — the server mirrors the second from the first
 * (HrPunchState.mode / .presence). This component only decides which control
 * to show:
 *
 *   HR off, a "Switch user" session,
 *   or /suites/hr/me unavailable   → the lead check-in pill, unchanged
 *                                    (checking in for leads is never lost)
 *   mode AVAILABILITY_ONLY         → the lead check-in pill (attendance cannot
 *                                    be recorded for this person right now)
 *   mode PUNCH                     → the attendance button below
 *   mode NONE                      → nothing
 *
 * So there is never more than one check-in control on the screen, and its
 * label is always the next action: Check in → Checked in (Start break / Check
 * out) → On break (End break) → Leads paused (Resume).
 */
export default function PresenceControl() {
  const impersonating = useIsImpersonating();
  const { enabled: hrOn } = useHrEnabled();
  if (!hrOn || impersonating) return <CheckInStatusPill />;
  return <HrPresenceControl />;
}

const PILL =
  "flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-[12px] font-medium transition-colors";
const PILL_OUT = "border-[#e2c78a] bg-[#fff6e3] text-[#6b4700] hover:bg-[#fff1d6] disabled:cursor-not-allowed disabled:opacity-70";
const PILL_IN =
  "border-[var(--color-green-5)] bg-[var(--color-green-1)] text-[var(--color-success)] hover:bg-[var(--color-green-2)] disabled:cursor-default disabled:opacity-80";

function HrPresenceControl() {
  const qc = useQueryClient();
  const me = useHrMe();
  const query = usePunchState({ enabled: !!me.data, placeholder: me.data?.punch ?? null });
  const { punch, isPending } = usePunch();
  const resume = useCheckInMutation();
  const state = query.data ?? me.data?.punch ?? null;
  const working = state?.state === "PUNCHED_IN";
  const now = useNow(30_000, working);

  // The HR bootstrap failed: fall back so checking in for leads still works.
  if (me.isError) return <CheckInStatusPill />;
  // An older server (no `mode` yet) keeps the old pill too.
  if (!state || state.mode === undefined) return me.isLoading ? null : <CheckInStatusPill />;
  if (state.mode === "AVAILABILITY_ONLY") return <CheckInStatusPill />;
  if (state.mode !== "PUNCH") return null;

  const busy = isPending || resume.isPending;
  const leads = state.presence?.applies === true;
  const elapsed = working ? minutesSince(query.dataUpdatedAt || undefined, now) : 0;
  const worked = fmtHours(state.workedTodayMinutes + elapsed);
  const since = state.openSinceMinuteIST != null ? fmtClockCompact(state.openSinceMinuteIST) : null;

  // ── Not checked in today (or checked out earlier): one tap.
  if (state.state !== "PUNCHED_IN" && state.state !== "ON_BREAK") {
    const title = !state.canPunchIn
      ? "You can't check in right now"
      : state.willBeLate
        ? `Shift started ${fmtClockCompact(state.shiftStartMinuteIST)} — this check-in will be marked late`
        : leads
          ? "Check in to record your attendance and start receiving leads"
          : "Check in to record your attendance";
    // A week-off or holiday never refuses the check-in — it is recorded and labelled.
    const offDay = state.dayNote === "HOLIDAY" ? "Holiday" : state.dayNote === "WEEK_OFF" ? "Week off" : null;
    return (
      <button
        type="button"
        data-testid="presence-control"
        data-state="out"
        disabled={busy || !state.canPunchIn}
        onClick={() => punch("IN")}
        title={offDay ? `${offDay} today — a check-in is still recorded. ${title}` : title}
        className={cn(PILL, PILL_OUT)}
      >
        <span className="size-2 rounded-full bg-[#d99a1f]" />
        <span className="hidden sm:inline">
          {offDay ?? (state.state === "PUNCHED_OUT" ? "Checked out" : "Not checked in")}
        </span>
        <span className="flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] text-[#6b4700] shadow-sm sm:ml-1">
          <LogIn className="size-3" />
          {busy ? "…" : "Check in"}
        </span>
      </button>
    );
  }

  const onBreak = state.state === "ON_BREAK";
  // At work, but lead availability stopped on its own (idle, a fixed stop time…).
  const leadsPaused = leads && !onBreak && state.presence.status === "CHECKED_OUT";

  const resumeLeads = () =>
    resume.mutate(undefined, {
      onSettled: () => {
        void qc.invalidateQueries({ queryKey: HR_KEYS.all });
      },
    });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="presence-control"
          data-state={onBreak ? "break" : leadsPaused ? "paused" : "in"}
          disabled={busy}
          className={cn(PILL, onBreak || leadsPaused ? PILL_OUT : PILL_IN)}
        >
          {onBreak ? (
            <Coffee className="size-3.5" />
          ) : (
            <span className={cn("size-2 rounded-full", leadsPaused ? "bg-[#d99a1f]" : "bg-[var(--color-success)]")} />
          )}
          {onBreak ? "On break" : leadsPaused ? "Leads paused" : "Checked in"}
          <span className="hidden text-[11px] font-normal opacity-80 md:inline">· {worked}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[230px]">
        <DropdownMenuLabel className="text-[12px] font-normal leading-snug text-[var(--color-text-secondary)]">
          {since ? `Checked in since ${since}` : "Checked in"} · {worked} today
          {leadsPaused && (
            <span className="mt-1 block text-[#8a5a00]">
              You are not receiving leads. {state.presence.pausedReason ?? ""}
            </span>
          )}
          {leads && !leadsPaused && !onBreak && <span className="mt-1 block">Receiving leads</span>}
          {onBreak && leads && <span className="mt-1 block">Leads are paused during a break</span>}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {leadsPaused && (
          <DropdownMenuItem onClick={resumeLeads}>
            <Play className="mr-2 h-4 w-4" />
            <span>Resume leads</span>
          </DropdownMenuItem>
        )}
        {onBreak ? (
          <DropdownMenuItem disabled={!state.canBreak} onClick={() => punch("BREAK_END")}>
            <Play className="mr-2 h-4 w-4" />
            <span>End break</span>
          </DropdownMenuItem>
        ) : (
          state.canBreak && (
            <DropdownMenuItem onClick={() => punch("BREAK_START")}>
              <Coffee className="mr-2 h-4 w-4" />
              <span>Start break</span>
            </DropdownMenuItem>
          )
        )}
        <DropdownMenuItem disabled={!state.canPunchOut} onClick={() => punch("OUT")}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>Check out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
